import React, { Component } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import UserIdentityPreview from "components/UserIdentityPreview";
import { MarketingCallStatusChip } from "components/MarketingCallStatus";

const getUserImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (String(value).startsWith("http")) {
    return value;
  }

  return `${Constants.imageDomain}${value}`;
};

const formatJoinedDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default class Users extends Component {
  state = {
    secondaryCategories: [],
    page_no: 1,
    per_page: 10,
    total_pages: 1,
    total_docs: 0,
    search: "",
    colleges: [],
    university_id: "",
    name_status: "all",
    start_date: "",
    end_date: "",
    loading: false,
    notiColor: "success",
    notification: false,
    message: "",
  };

  componentDidMount() {
    this.getAllUsersList();
    this.getAllColleges();
  }

  showNotification = (message, color = "success") => {
    this.setState({
      notification: true,
      notiColor: color,
      message,
    });

    setTimeout(() => {
      this.setState({
        notification: false,
        message: "",
      });
    }, 2500);
  };

  getAllUsersList = (page = this.state.page_no, search = this.state.search) => {
    if (
      this.state.start_date &&
      this.state.end_date &&
      this.state.start_date > this.state.end_date
    ) {
      this.showNotification("Joined from date cannot be after joined to date.", "error");
      return;
    }

    this.setState({ loading: true });
    if (init() === "success") {
      axios
        .get(Constants.getUrls.getAllUsersList, {
          params: {
            page,
            limit: this.state.per_page,
            search: search.trim(),
            university_id: this.state.university_id,
            name_status: this.state.name_status,
            start_date: this.state.start_date,
            end_date: this.state.end_date,
          },
        })
        .then((resp) => {
          const payload = resp?.data?.data || {};
          this.setState({
            secondaryCategories: payload.docs || [],
            page_no: payload.page || page,
            per_page: payload.limit || this.state.per_page,
            total_pages: payload.totalPages || 1,
            total_docs: payload.totalDocs || 0,
            loading: false,
          });
        })
        .catch((err) => {
          this.setState({ loading: false });
          this.showNotification(
            err?.response?.data?.message || "Something went wrong!",
            "warning"
          );
        });
    } else {
      this.setState({
        loading: false,
      });
    }
  };

  getAllColleges = () => {
    if (init() !== "success") {
      return;
    }

    axios
      .get(Constants.getUrls.getAllUniversities, {
        params: { page: 1, limit: 1000 },
      })
      .then((resp) => {
        const colleges = resp?.data?.universities || resp?.data?.data?.docs || [];
        this.setState({ colleges: Array.isArray(colleges) ? colleges : [] });
      })
      .catch((err) => {
        this.showNotification(
          err?.response?.data?.message || "Unable to load college filters.",
          "warning"
        );
      });
  };

  clearFilters = () => {
    this.setState(
      {
        search: "",
        university_id: "",
        name_status: "all",
        start_date: "",
        end_date: "",
        page_no: 1,
      },
      () => this.getAllUsersList(1, "")
    );
  };

  handleEntriesPerPageChange = (newLimit) => {
    this.setState({ per_page: newLimit, page_no: 1 }, () => {
      this.getAllUsersList(1, this.state.search);
    });
  };

  render() {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                All Users
              </MDTypography>
            </MDBox>

            <MDBox px={2} py={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Search by name, mobile, email, referral code"
                    value={this.state.search}
                    onChange={(e) => this.setState({ search: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        this.getAllUsersList(1, this.state.search);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>College</InputLabel>
                    <Select
                      value={this.state.university_id}
                      label="College"
                      onChange={(e) => this.setState({ university_id: e.target.value })}
                    >
                      <MenuItem value="">All Colleges</MenuItem>
                      <MenuItem value="none">No College Added</MenuItem>
                      {this.state.colleges.map((college) => (
                        <MenuItem key={college?._id} value={college?._id}>
                          {college?.short_name || college?.name || "Unnamed College"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Name Status</InputLabel>
                    <Select
                      value={this.state.name_status}
                      label="Name Status"
                      onChange={(e) => this.setState({ name_status: e.target.value })}
                    >
                      <MenuItem value="all">All Users</MenuItem>
                      <MenuItem value="missing">Users With No Name</MenuItem>
                      <MenuItem value="present">Users With Name</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Joined From"
                    InputLabelProps={{ shrink: true }}
                    value={this.state.start_date}
                    onChange={(e) => this.setState({ start_date: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Joined To"
                    InputLabelProps={{ shrink: true }}
                    value={this.state.end_date}
                    onChange={(e) => this.setState({ end_date: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton
                      variant="contained"
                      color="info"
                      onClick={() => this.getAllUsersList(1, this.state.search)}
                    >
                      Apply Filters
                    </MDButton>
                    <MDButton variant="outlined" color="secondary" onClick={this.clearFilters}>
                      Clear Filters
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox py={3}>
              {this.state.loading ? (
                <MDBox py={4} textAlign="center">
                  <MDTypography variant="h6" color="white">
                    Loading users...
                  </MDTypography>
                </MDBox>
              ) : this.state.secondaryCategories.length > 0 ? (
                <>
                  <MDBox px={2} pb={1.25} display="flex" alignItems="center" gap={1}>
                    <MDBox
                      width="10px"
                      height="10px"
                      borderRadius="50%"
                      sx={{
                        backgroundColor: "#55D68E",
                        boxShadow: "0 0 0 4px rgba(85,214,142,0.12)",
                      }}
                    />
                    <MDTypography variant="caption" sx={{ color: "#8FA7C4" }}>
                      Light green rows have feedback recorded
                    </MDTypography>
                  </MDBox>
                  <DataTable
                    fitContainer
                    entriesPerPage={{
                      defaultValue: this.state.per_page,
                      entries: [10, 25, 50, 100],
                    }}
                    showTotalEntries
                    table={{
                      columns: [
                        { Header: "#", accessor: "serial", width: "5%" },
                        { Header: "user", accessor: "user_name", width: "19%" },
                        { Header: "contact", accessor: "contact", width: "23%" },
                        { Header: "college", accessor: "college", width: "18%" },
                        { Header: "joined", accessor: "joined_at", width: "14%" },
                        { Header: "tests", accessor: "test_attempted", width: "7%" },
                        { Header: "call", accessor: "call_status", width: "14%" },
                      ],
                      rows: this.state.secondaryCategories.map((sec, index) => ({
                        _rowSx: sec?.has_marketing_feedback
                          ? {
                              background:
                                "linear-gradient(90deg, rgba(40,183,111,0.16) 0%, rgba(40,183,111,0.07) 52%, rgba(40,183,111,0.025) 100%)",
                              "& > td:first-of-type": {
                                boxShadow: "inset 3px 0 0 #55D68E",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(40,183,111,0.18)",
                              },
                            }
                          : {
                              "&:hover": { backgroundColor: "rgba(126,170,220,0.045)" },
                            },
                        serial: (this.state.page_no - 1) * this.state.per_page + index + 1,
                        user_name: (
                          <Link to={`/user-details/${sec?._id}`} style={{ textDecoration: "none" }}>
                            <UserIdentityPreview
                              name={String(sec?.name || "").trim() || "-"}
                              imageUrl={getUserImageUrl(sec?.profile_pic_url)}
                              imageSize={42}
                            />
                          </Link>
                        ),
                        contact: (
                          <MDBox minWidth={0}>
                            <MDTypography
                              variant="button"
                              display="block"
                              sx={{ color: "#D7E5F5", lineHeight: 1.35 }}
                            >
                              {sec?.mobile || "-"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              display="block"
                              sx={{
                                color: "#8FA7C4 !important",
                                lineHeight: 1.35,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {sec?.email || "No email"}
                            </MDTypography>
                          </MDBox>
                        ),
                        college: (
                          <MDTypography
                            variant="caption"
                            sx={{ color: "#C7D8EB", overflowWrap: "anywhere", lineHeight: 1.35 }}
                          >
                            {sec?.university_short_name || sec?.university_name || "Not added"}
                          </MDTypography>
                        ),
                        joined_at: formatJoinedDate(sec?.createdAt),
                        test_attempted: sec?.test_solved || 0,
                        call_status: (
                          <MarketingCallStatusChip status={sec?.marketing_call_status} />
                        ),
                      })),
                    }}
                    isSorted={false}
                    page_no={this.state.page_no}
                    totalPages={this.state.total_pages}
                    totalDocs={this.state.total_docs}
                    onPageChange={(page) => this.getAllUsersList(page, this.state.search)}
                    onEntriesPerPageChange={this.handleEntriesPerPageChange}
                  />
                </>
              ) : (
                <MDBox px={2} pb={1}>
                  <MDTypography variant="button" color="text">
                    No users match the selected filters.
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Agriquiz"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
